import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ArrowLeftIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  MapPinIcon,
  StarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const ContractorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [reviewsLimit, setReviewsLimit] = useState(5);

  // 소장 정보 조회
  const { data: contractor, isLoading } = useQuery(
    ['contractor', id],
    () => api.get(`/contractors/${id}`).then(res => res.data)
  );

  // 소장 통계 조회
  const { data: stats } = useQuery(
    ['contractor-stats', id],
    () => api.get(`/contractors/${id}/stats`).then(res => res.data),
    {
      enabled: !!id
    }
  );

  // 소장 리뷰 조회
  const { data: reviewsData } = useQuery(
    ['contractor-reviews', id],
    () => api.get(`/contractors/${id}/reviews`).then(res => res.data),
    {
      enabled: !!id
    }
  );

  const reviews = Array.isArray(reviewsData) ? reviewsData : reviewsData?.reviews || [];

  // 평가 가능한 프로젝트 조회
  const { data: reviewableProjects } = useQuery(
    ['reviewable-projects', id],
    () => api.get(`/reviews/contractors/${id}/reviewable`).then(res => res.data),
    {
      enabled: !!id && (user?.user_type === 'customer' || user?.user_type === 'vip_customer')
    }
  );

  // 리뷰 생성
  const createReviewMutation = useMutation(
    (data) => api.post(`/reviews/projects/${data.projectId}`, {
      rating: data.rating,
      comment: data.comment
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contractor-reviews', id]);
        queryClient.invalidateQueries(['contractor-stats', id]);
        queryClient.invalidateQueries(['contractor', id]);
        queryClient.invalidateQueries(['reviewable-projects', id]);
        setShowReviewModal(false);
        setSelectedProject(null);
        setRating(5);
        setComment('');
        alert('평가가 등록되었습니다.');
      },
      onError: (error) => {
        alert(error.response?.data?.error || '평가 등록에 실패했습니다.');
      }
    }
  );

  const handleSubmitReview = () => {
    if (!selectedProject) {
      alert('평가할 프로젝트를 선택해주세요.');
      return;
    }
    createReviewMutation.mutate({
      projectId: selectedProject,
      rating,
      comment
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0원';
    const billion = Math.floor(amount / 100000000);
    const million = Math.floor((amount % 100000000) / 10000);
    
    if (billion > 0) {
      return `${billion}억${million > 0 ? ` ${million}만원` : '원'}`;
    } else if (million > 0) {
      return `${million}만원`;
    } else {
      return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => {
      if (i < Math.floor(rating)) {
        return <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" />;
      } else {
        return <StarIcon key={i} className="h-5 w-5 text-gray-300" />;
      }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">소장 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">소장 정보를 찾을 수 없습니다.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-500"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-gradient-to-b from-blue-600 to-blue-700 text-white pb-20">
        <div className="px-4 pt-4 pb-6">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          
          {/* 프로필 정보 */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <UserCircleIcon className="h-12 w-12 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">{contractor.name} 소장님</h1>
                {(user?.user_type === 'customer' || user?.user_type === 'vip_customer') && (
                  <button
                    onClick={() => setShowReviewModal(true)}
                    className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm font-medium transition-colors"
                  >
                    평가하기
                  </button>
                )}
              </div>
              {/* 회사명 - 소장/관리자만 볼 수 있음 */}
              {(user?.user_type === 'contractor' || user?.user_type === 'admin') && (
                <p className="text-blue-100 mt-1">{contractor.company_name}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                {renderStars(contractor.rating || 0)}
                <span className="text-sm text-blue-100">
                  ({contractor.rating?.toFixed(1) || '0.0'})
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 통계 카드 - 간소화 */}
      <div className="px-4 -mt-12">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-blue-600" />
            프로젝트 통계
          </h2>
          
          {/* 총 프로젝트 한줄 표시 */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">총 프로젝트</span>
              <span className="text-lg font-bold text-gray-900">{stats?.total || 0}건</span>
            </div>
          </div>

          {/* 빠른 응답 한줄 표시 */}
          <div className="flex items-center gap-3 px-2">
            <ClockIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-gray-700">
              빠른 응답 <span className="font-medium text-gray-900">· 평균 30분 이내</span>
            </span>
          </div>
        </div>
      </div>

      {/* 소장 정보 */}
      <div className="px-4 mt-6">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">소장 정보</h2>
          
          <div className="space-y-4">
            {/* 연락처 - 소장/관리자만 볼 수 있음 */}
            {contractor.phone && (user?.user_type === 'contractor' || user?.user_type === 'admin') && (
              <div className="flex items-center gap-3">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">연락처</p>
                  <p className="font-medium text-gray-900">{contractor.phone}</p>
                </div>
              </div>
            )}
            
            {contractor.specialties && (
              <div className="flex items-start gap-3">
                <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600 mb-1">전문 분야</p>
                  <div className="flex flex-wrap gap-2">
                    {contractor.specialties.split(',').map((specialty, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                      >
                        {specialty.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 고객 평가 */}
      {reviews.length > 0 && (
        <div className="px-4 mt-6 mb-20">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">고객 평가</h2>
            
            <div className="space-y-4">
              {reviews.slice(0, reviewsLimit).map((review, index) => (
                <div key={index} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      {renderStars(review.rating)}
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-gray-700">{review.comment}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {review.customer_name} 고객님
                  </p>
                </div>
              ))}
            </div>

            {reviews.length > reviewsLimit ? (
              <button
                onClick={() => setReviewsLimit(reviewsLimit + 5)}
                className="mt-4 w-full py-2 text-center text-blue-600 font-medium hover:text-blue-700"
              >
                평가 더보기 ({reviews.length - reviewsLimit}개 남음)
              </button>
            ) : reviews.length > 5 && reviewsLimit > 5 && (
              <button
                onClick={() => setReviewsLimit(5)}
                className="mt-4 w-full py-2 text-center text-gray-600 font-medium hover:text-gray-700"
              >
                접기
              </button>
            )}
          </div>
        </div>
      )}

      {/* 평가하기 모달 */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">소장님 평가하기</h2>
              
              {/* 프로젝트 선택 */}
              {reviewableProjects && reviewableProjects.filter(p => !p.hasReview).length > 0 ? (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      평가할 프로젝트 선택
                    </label>
                    <select
                      value={selectedProject || ''}
                      onChange={(e) => setSelectedProject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">프로젝트를 선택하세요</option>
                      {reviewableProjects
                        .filter(p => !p.hasReview)
                        .map(project => (
                          <option key={project.id} value={project.id}>
                            {project.title}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* 별점 선택 */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      별점
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          className="p-1"
                        >
                          {star <= rating ? (
                            <StarIconSolid className="h-8 w-8 text-yellow-400" />
                          ) : (
                            <StarIcon className="h-8 w-8 text-gray-300" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 평가 내용 */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      평가 내용 (선택)
                    </label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="소장님과의 작업 경험을 공유해주세요"
                    />
                  </div>

                  {/* 버튼 */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowReviewModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSubmitReview}
                      disabled={!selectedProject || createReviewMutation.isLoading}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300"
                    >
                      {createReviewMutation.isLoading ? '등록 중...' : '평가 등록'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">
                    평가 가능한 완료된 프로젝트가 없습니다.
                  </p>
                  <button
                    onClick={() => setShowReviewModal(false)}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    닫기
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorProfile;